import type { CommercialRelationship, BookingMode } from "@/lib/experiences";

export type PublicExperienceCommerce = {
  experience_id?: string | null;
  offer_id?: string | null;
  placement_id?: string | null;
  placement_target_type?: "guide" | "transport_page" | null;
  operator_id?: string | null;
  external_product_key?: string | null;
  legacy_card_id?: string | null;
  url?: string | null;
  affiliate_url?: string | null;
  booking_url?: string | null;
  booking_mode?: BookingMode | null;
  commercial_relationship?: CommercialRelationship | null;
  cta_label?: string | null;
  provider?: string | null;
  provider_key?: string | null;
  offer_provider_key?: string | null;
  operator_name?: string | null;
  supplier_name?: string | null;
  promotion_status?: "editorial" | "sponsored" | null;
  commercial_source?: "curator" | "teravia" | null;
};

const FALLBACK_CTA: Record<BookingMode, string> = {
  instant_booking: "Check availability",
  request_availability: "Request availability",
  contact_operator: "Contact operator",
  official_booking: "Book on official site",
};

export function experienceBookingUrl(experience: PublicExperienceCommerce) {
  return experience.booking_url || experience.affiliate_url || experience.url || null;
}

export function experienceCta(experience: PublicExperienceCommerce, locale?: string) {
  const explicit = experience.cta_label?.trim();
  if (explicit) return explicit;
  const mode = experience.booking_mode;
  if (String(locale || "en").slice(0, 2).toLowerCase() === "it") {
    return ({ instant_booking: "Verifica disponibilità", request_availability: "Richiedi disponibilità", contact_operator: "Contatta l'operatore", official_booking: "Prenota sul sito ufficiale" } satisfies Record<BookingMode, string>)[mode || "instant_booking"];
  }
  return mode ? FALLBACK_CTA[mode] : "Check availability";
}

export function experienceIsCommercial(experience: PublicExperienceCommerce) {
  return experience.promotion_status === "sponsored" ||
    experience.commercial_relationship === "affiliate_network" ||
    experience.commercial_relationship === "direct_referral";
}

export function experienceLinkRel(experience: PublicExperienceCommerce) {
  return experienceIsCommercial(experience)
    ? "sponsored noopener noreferrer"
    : "noopener noreferrer";
}

export function experienceProviderLabel(experience: PublicExperienceCommerce) {
  if (experience.operator_name?.trim()) return experience.operator_name.trim();
  if (experience.supplier_name?.trim()) return experience.supplier_name.trim();
  return null;
}

function experienceBookingPartnerLabel(experience: PublicExperienceCommerce) {
  const provider = experience.offer_provider_key || experience.provider_key || experience.provider;
  if (!provider) return experienceProviderLabel(experience);
  return provider.toLocaleLowerCase() === "viator"
    ? "Viator"
    : provider.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type DisclosureCopy = {
  sponsored: string;
  affiliate: (partner: string) => string;
  referral: string;
};

function disclosureCopy(locale?: string): DisclosureCopy {
  const language = String(locale || "en").slice(0, 2).toLowerCase();
  const copy: Record<string, DisclosureCopy> = {
    da: { sponsored: "Sponsoreret anbefaling.", affiliate: (partner) => `Partnerlink · Teravia kan modtage en kommission fra ${partner}, uden ekstra omkostninger for dig.`, referral: "Henvisningslink · Teravia kan modtage en kommission, uden ekstra omkostninger for dig." },
    de: { sponsored: "Gesponserte Empfehlung.", affiliate: (partner) => `Partnerlink · Teravia kann von ${partner} eine Provision erhalten, ohne Mehrkosten für dich.`, referral: "Empfehlungslink · Teravia kann eine Provision erhalten, ohne Mehrkosten für dich." },
    es: { sponsored: "Recomendación patrocinada.", affiliate: (partner) => `Enlace de afiliado · Teravia puede recibir una comisión de ${partner}, sin coste adicional para ti.`, referral: "Enlace de recomendación · Teravia puede recibir una comisión, sin coste adicional para ti." },
    fr: { sponsored: "Recommandation sponsorisée.", affiliate: (partner) => `Lien affilié · Teravia peut recevoir une commission de ${partner}, sans frais supplémentaires pour vous.`, referral: "Lien de recommandation · Teravia peut recevoir une commission, sans frais supplémentaires pour vous." },
    it: { sponsored: "Consiglio sponsorizzato.", affiliate: (partner) => `Link affiliato · Teravia può ricevere una commissione da ${partner}, senza costi aggiuntivi per te.`, referral: "Link di segnalazione · Teravia può ricevere una commissione, senza costi aggiuntivi per te." },
    ja: { sponsored: "スポンサー付きのおすすめです。", affiliate: (partner) => `アフィリエイトリンク · 予約すると、追加費用なしでTeraviaが${partner}から手数料を受け取る場合があります。`, referral: "紹介リンク · 予約すると、追加費用なしでTeraviaが手数料を受け取る場合があります。" },
    nl: { sponsored: "Gesponsorde aanbeveling.", affiliate: (partner) => `Affiliatelink · Teravia kan een commissie van ${partner} ontvangen, zonder extra kosten voor jou.`, referral: "Verwijzingslink · Teravia kan een commissie ontvangen, zonder extra kosten voor jou." },
    pt: { sponsored: "Recomendação patrocinada.", affiliate: (partner) => `Link de afiliado · A Teravia pode receber uma comissão de ${partner}, sem custos adicionais para si.`, referral: "Link de recomendação · A Teravia pode receber uma comissão, sem custos adicionais para si." },
    sv: { sponsored: "Sponsrad rekommendation.", affiliate: (partner) => `Partnerlänk · Teravia kan få provision från ${partner}, utan extra kostnad för dig.`, referral: "Hänvisningslänk · Teravia kan få provision, utan extra kostnad för dig." },
  };
  return copy[language] || {
    sponsored: "Sponsored recommendation.",
    affiliate: (partner) => `Affiliate link · Teravia may earn a commission from ${partner}, at no extra cost to you.`,
    referral: "Referral link · Teravia may earn a commission, at no extra cost to you.",
  };
}

export function experienceDisclosure(experience: PublicExperienceCommerce, locale?: string, context: "guide" | "editorial" = "guide") {
  const copy = disclosureCopy(locale);
  if (experience.promotion_status === "sponsored" && experience.commercial_relationship === "none") {
    return copy.sponsored;
  }
  if (experience.commercial_relationship === "affiliate_network") {
    const partner = experienceBookingPartnerLabel(experience) || "booking partner";
    if (context === "editorial" || experience.placement_target_type === "transport_page") return copy.affiliate(partner);
    if (experience.commercial_source === "curator") return `Affiliate link · This Guide's creator may earn a commission from ${partner}, at no extra cost to you.`;
    if (!experience.commercial_source && experience.placement_id) return `Affiliate link · The Guide's creator or Teravia may earn a commission from ${partner}, at no extra cost to you.`;
    return copy.affiliate(partner);
  }
  if (experience.commercial_relationship === "direct_referral") {
    return copy.referral;
  }
  return null;
}
